import { RequestReviewAction } from './actions';
import {
  organizationSelector,
  isOrganizationOwner,
} from 'selectors/Organization';
import { select, takeEvery, put, all, call } from 'redux-saga/effects';
import { selectors as billingSelectors } from 'reducers/billing';
import { generators, selectors, actions } from 'stores';
import {
  Organization,
  Contributor,
  Billing,
} from 'stores/organizations/models';
import { PullRequest } from 'stores/pullRequests/models';
import { actions as contributorsActions } from 'reducers/Contributors';
import {
  REVIEW_REQUEST_PAYMENT_NEEDED_SHOW,
  REVIEW_REQUEST_POSTING_USER_NEEDED_SHOW,
  REVIEWS_REQUEST_CLICK,
  logEvent,
} from 'utils/analytics';
import { APIResult } from 'Common/utilities/api';

export function throwError(text: string) {
  throw new Error(text);
}

export function* checkRequestReview({ pullRequestUUID }: RequestReviewAction) {
  const organization: Organization = yield select(organizationSelector);
  if (!organization) {
    yield call(throwError, 'No organization selected.');
    return;
  }

  const pullRequest: PullRequest = yield select(
    selectors.pullRequests.entryData,
    pullRequestUUID
  );
  if (!pullRequest) {
    yield call(throwError, `PullRequest ${pullRequestUUID} not found.`);
    return;
  }

  const isOptInBilling: boolean = yield select(
    billingSelectors.isOptInBilling,
    organization.uuid
  );
  const contributor: Contributor = yield select(
    selectors.organizations.contributors.contributor,
    pullRequest.user_name
  );
  const isBilled = Boolean(contributor && contributor.has_seat);
  if (isOptInBilling) {
    const isStaticUsers: boolean = yield select(
      billingSelectors.isStaticUsers,
      organization.uuid
    );
    const availableSeats: number = yield select(
      billingSelectors.getAvailableSeats,
      organization.uuid
    );
    if (isStaticUsers && !isBilled && availableSeats === 0) {
      yield put(
        actions.reviews.modals.showReviewModal({ modalType: 'NO_SEATS' })
      );
      return;
    }

    const isOwner: boolean = yield select(isOrganizationOwner);
    if (!isStaticUsers && !isBilled) {
      yield put(
        actions.reviews.modals.showReviewModal({
          modalType: isOwner ? 'WILL_ADD_CONTRIBUTOR' : 'CAN_NOT_ADD_SEAT',
          pullRequestUUID,
        })
      );
      return;
    }
  }

  const billing: Billing = yield select(
    billingSelectors.billingInfoByUUID,
    organization.uuid
  );
  const isGithubPlan: boolean = yield select(
    billingSelectors.isGithubPlan,
    organization.uuid
  );
  if (
    !billing ||
    (!isGithubPlan && !billing.card_last_four && !billing.has_invoice_billing)
  ) {
    yield all([
      call(logEvent, REVIEW_REQUEST_PAYMENT_NEEDED_SHOW),
      put(
        actions.reviews.modals.showReviewModal({ modalType: 'NEEDS_PAYMENT' })
      ),
    ]);
    return;
  }

  if (pullRequest && pullRequest.status !== 'open') {
    /**
     * If the PR is merged, a warning modal will appear when attempting to request a review.
     * This stores the project & pullRequest in state so that we can
     * request the review from the modal
     */
    yield put(
      actions.reviews.modals.showReviewModal({
        modalType: 'MERGED_REVIEW',
        pullRequestUUID,
      })
    );
    return;
  }

  const shouldShowTrialModal: boolean = yield select(
    billingSelectors.shouldShowTrialModal,
    organization.uuid
  );
  if (shouldShowTrialModal) {
    yield put(
      actions.reviews.modals.showReviewModal({
        modalType: 'TRIAL',
        pullRequestUUID,
      })
    );
    return;
  }

  const shouldShowSubscriptionModal: boolean = yield select(
    billingSelectors.shouldShowSubscriptionModal,
    organization.uuid
  );
  if (shouldShowSubscriptionModal) {
    yield put(
      actions.reviews.modals.showReviewModal({
        modalType: 'SUBSCRIPTION',
        pullRequestUUID,
      })
    );
    return;
  }

  yield call(logEvent, REVIEWS_REQUEST_CLICK);
  yield put(actions.reviews.requestReview(pullRequestUUID));
}

export function* requestReview({
  pullRequestUUID,
  h1ReportID,
}: RequestReviewAction) {
  const organization: Organization = yield select(organizationSelector);
  if (!organization) {
    yield call(throwError, 'No organization selected.');
    return;
  }

  yield put(
    generators.pullRequests.toggleReview.request({
      uuid: pullRequestUUID,
      enabled: true,
      h1ReportID,
      callback: requestReviewCallback,
    })
  );
}

export function* requestReviewCallback(response: APIResult) {
  if (response.success) {
    return;
  }

  switch (response.status) {
    case 402:
      yield all([
        put(
          actions.reviews.modals.showReviewModal({ modalType: 'NEEDS_PAYMENT' })
        ),
        call(logEvent, REVIEW_REQUEST_PAYMENT_NEEDED_SHOW),
      ]);
      return;
    case 409:
      yield all([
        put(
          actions.reviews.modals.showReviewModal({
            modalType: 'NEEDS_POSTING_USER',
          })
        ),
        call(logEvent, REVIEW_REQUEST_POSTING_USER_NEEDED_SHOW),
      ]);
      return;
  }
}

export function* sagas() {
  yield takeEvery('REVIEWS/REQUEST_REVIEW', requestReview);
  yield takeEvery('REVIEWS/CHECK_REQUEST_REVIEW', checkRequestReview);
}
